import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import { showToast } from '../../../helpers';
import {
  apiMessage,
  buildSlots,
  createOpsSlotSheet,
  fmtDay,
  fmtTime,
  getOpsSlotSheets,
  patchOpsSlotSheet,
  removeOpsSlotBooking,
  SLOT_KINDS,
  TSlotInput,
  TSlotKind,
} from '../../../requests/opsSlots';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  labelClass,
  Pill,
} from '../ui';
import { fieldClass, TIME_ZONES } from './shared';

/**
 * "Offer times" — the sign-up sheet a CSM sends instead of a SignUpGenius
 * link. Build the slots, create the sheet, copy the link; bookings appear
 * here as they come in. A prep-call booking sets the prep call status above
 * to Scheduled; a coaching booking also lands on the roster.
 */
const OfferTimesCard: React.FC<{
  classId: string;
  className: string;
  timeZone: string;
}> = ({ classId, className, timeZone }) => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['opsSlotSheets', classId],
    queryFn: () => getOpsSlotSheets(classId),
  });
  const sheets = data?.items ?? [];

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<TSlotKind>('prep-call');
  const [title, setTitle] = useState('');
  const [tz, setTz] = useState(timeZone || 'PST');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [length, setLength] = useState('30');
  const [gap, setGap] = useState('0');
  const [capacity, setCapacity] = useState('1');
  const [slots, setSlots] = useState<TSlotInput[]>([]);
  const [expanded, setExpanded] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    const what =
      kind === 'prep-call'
        ? 'Prep call'
        : kind === 'coaching'
        ? 'Coaching'
        : '';
    setTitle(what ? `${what} — ${className}` : className);
  }, [kind, open, className]);
  useEffect(() => {
    if (timeZone) setTz(timeZone);
  }, [timeZone]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['opsSlotSheets', classId] });

  const addTimes = () => {
    const built = buildSlots({
      date,
      startTime,
      endTime,
      lengthMinutes: Number(length),
      gapMinutes: Number(gap),
      capacity: Number(capacity),
    });
    if (built.length === 0) {
      showToast(
        'Pick a date, a window that ends after it starts, and a length of at least 5 minutes',
        'error',
      );
      return;
    }
    setSlots((x) =>
      [...x, ...built.filter((b) => !x.some((e) => e.start === b.start))].sort(
        (a, b) => a.start.localeCompare(b.start),
      ),
    );
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Link copied', 'success');
    } catch {
      showToast('Could not copy — select the link and copy it', 'error');
    }
  };

  const create = useMutation({
    mutationFn: () =>
      createOpsSlotSheet({
        kind,
        title: title.trim(),
        recordObject: 'class',
        recordId: classId,
        timeZone: tz || undefined,
        slots,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (sheet) => {
      showToast(
        `Sheet created · ${sheet.slots.length} times offered`,
        'success',
      );
      copy(sheet.publicLink);
      setOpen(false);
      setSlots([]);
      setNotes('');
      setExpanded(sheet.id);
      invalidate();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const toggleClosed = useMutation({
    mutationFn: (v: { id: string; closed: boolean }) =>
      patchOpsSlotSheet(v.id, { closed: v.closed }),
    onSuccess: (sheet) => {
      showToast(sheet.closed ? 'Sheet closed' : 'Sheet reopened', 'success');
      invalidate();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const unbook = useMutation({
    mutationFn: (v: { id: string; slotId: string; email: string }) =>
      removeOpsSlotBooking(v.id, v.slotId, v.email),
    onSuccess: () => {
      showToast('Booking removed', 'success');
      invalidate();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const kindLabel = (k: TSlotKind) =>
    SLOT_KINDS.find((x) => x.value === k)?.label ?? k;

  return (
    <Card
      title={`Offer times · ${sheets.length} ${
        sheets.length === 1 ? 'sheet' : 'sheets'
      }`}
      right={
        <button
          type="button"
          className={`${btnGhost} text-xs`}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'close' : '+ new sheet'}
        </button>
      }
    >
      {open && (
        <div className="border-b border-white-three p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Kind</label>
              <select
                className={fieldClass}
                value={kind}
                onChange={(e) => setKind(e.target.value as TSlotKind)}
              >
                {SLOT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-xs text-gray-bold">
                {SLOT_KINDS.find((k) => k.value === kind)?.hint}
              </div>
            </div>
            <div>
              <label className={labelClass}>
                Time zone shown to the booker
              </label>
              <select
                className={fieldClass}
                value={tz}
                onChange={(e) => setTz(e.target.value)}
              >
                {TIME_ZONES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Title (what the booker sees)</label>
              <input
                className={fieldClass}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Notes for the booker</label>
              <textarea
                className={fieldClass}
                rows={2}
                placeholder="Who the call is with, what to have ready, how to join"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-grey-50 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-bold">
              Quick slot builder
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <div className="col-span-2">
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  className={fieldClass}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>From</label>
                <input
                  type="time"
                  className={fieldClass}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>To</label>
                <input
                  type="time"
                  className={fieldClass}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Length (min)</label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  className={fieldClass}
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Seats per slot</label>
                <input
                  type="number"
                  min="1"
                  className={fieldClass}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              </div>
              <div>
                <label className={labelClass}>Gap (min)</label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  className={fieldClass}
                  value={gap}
                  onChange={(e) => setGap(e.target.value)}
                />
              </div>
              <div className="col-span-2 flex items-end md:col-span-5">
                <button
                  type="button"
                  className={btnSecondary}
                  disabled={!date}
                  onClick={addTimes}
                >
                  Add these times
                </button>
                <span className="ml-3 text-xs text-gray-bold">
                  Add several days by changing the date and adding again.
                </span>
              </div>
            </div>
          </div>

          {slots.length > 0 && (
            <div className="mt-4">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-bold">
                {slots.length} {slots.length === 1 ? 'time' : 'times'} to offer
              </div>
              <div className="flex flex-wrap gap-1.5">
                {slots.map((sl) => (
                  <span
                    key={sl.start}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white-two bg-white px-2.5 py-1 text-xs text-gray-five"
                    title={fmtDay(sl.start)}
                  >
                    {fmtDay(sl.start).split(',')[0]} {fmtTime(sl.start)}
                    {(sl.capacity ?? 1) > 1 ? ` ×${sl.capacity}` : ''}
                    <button
                      type="button"
                      className="text-gray-bold hover:text-red-one"
                      aria-label="remove"
                      onClick={() =>
                        setSlots((x) => x.filter((e) => e.start !== sl.start))
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className={`${btnGhost} text-xs`}
                  onClick={() => setSlots([])}
                >
                  clear
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              className={btnPrimary}
              disabled={create.isPending || slots.length === 0 || !title.trim()}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create sheet and copy link'}
            </button>
            <span className="text-xs text-gray-bold">
              The link opens without a login; anyone holding it can book.
            </span>
          </div>
        </div>
      )}

      <div className="p-5 text-sm">
        {sheets.length === 0 ? (
          <div className="text-gray-bold">
            No sign-up sheet yet. Offer times for the prep call or for 1:1
            coaching and send the link instead of a SignUpGenius.
          </div>
        ) : (
          <div className="space-y-4">
            {sheets.map((sh) => {
              const isOpen = expanded === sh.id;
              return (
                <div
                  key={sh.id}
                  className="rounded-lg border border-white-three"
                >
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                    <b className="text-gray-four">{sh.title}</b>
                    <Pill value={kindLabel(sh.kind)} tone="info" />
                    {sh.closed && <Pill value="Closed" tone="neutral" />}
                    <span className="text-xs text-gray-bold">
                      {sh.bookings} of {sh.seats} seats booked ·{' '}
                      {sh.slots.length}{' '}
                      {sh.slots.length === 1 ? 'time' : 'times'}
                      {sh.timeZone ? ` · ${sh.timeZone}` : ''}
                    </span>
                    <span className="ml-auto flex items-center gap-1">
                      <button
                        type="button"
                        className={`${btnGhost} text-xs`}
                        onClick={() => setExpanded(isOpen ? '' : sh.id)}
                      >
                        {isOpen ? 'hide' : 'bookings'}
                      </button>
                      <button
                        type="button"
                        className={`${btnGhost} text-xs`}
                        disabled={toggleClosed.isPending}
                        onClick={() =>
                          toggleClosed.mutate({ id: sh.id, closed: !sh.closed })
                        }
                      >
                        {sh.closed ? 'reopen' : 'close'}
                      </button>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 border-t border-white-three px-4 py-2">
                    <input
                      readOnly
                      className={`${fieldClass} text-xs text-gray-three`}
                      value={sh.publicLink}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      type="button"
                      className={`${btnSecondary} whitespace-nowrap`}
                      onClick={() => copy(sh.publicLink)}
                    >
                      Copy link
                    </button>
                    <a
                      className={`${btnGhost} whitespace-nowrap`}
                      href={sh.publicLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      open
                    </a>
                  </div>
                  {isOpen && (
                    <div className="border-t border-white-three px-4 py-3">
                      <table className="w-full text-left text-xs">
                        <thead className="text-[11px] uppercase tracking-wider text-gray-bold">
                          <tr>
                            <th className="py-1 pr-3 font-semibold">Time</th>
                            <th className="py-1 pr-3 font-semibold">Seats</th>
                            <th className="py-1 font-semibold">Booked</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sh.slots.map((sl) => (
                            <tr
                              key={sl.id}
                              className="border-t border-white-three align-top"
                            >
                              <td className="whitespace-nowrap py-1.5 pr-3 text-gray-five">
                                {fmtDay(sl.start)} · {fmtTime(sl.start)}–
                                {fmtTime(sl.end)}
                              </td>
                              <td className="whitespace-nowrap py-1.5 pr-3 text-gray-five [font-variant-numeric:tabular-nums]">
                                {sl.capacity - sl.remaining}/{sl.capacity}
                              </td>
                              <td className="py-1.5">
                                {sl.bookings.length === 0 ? (
                                  <span className="text-gray-two">—</span>
                                ) : (
                                  <div className="space-y-1">
                                    {sl.bookings.map((b) => (
                                      <div
                                        key={b.email}
                                        className="flex flex-wrap items-center gap-x-2"
                                      >
                                        <b className="text-gray-four">
                                          {b.name}
                                        </b>
                                        <span className="text-gray-bold">
                                          {b.email}
                                        </span>
                                        {b.note && (
                                          <span className="basis-full text-gray-three">
                                            “{b.note}”
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          className="text-red-one hover:underline"
                                          disabled={unbook.isPending}
                                          onClick={() =>
                                            unbook.mutate({
                                              id: sh.id,
                                              slotId: sl.id,
                                              email: b.email,
                                            })
                                          }
                                        >
                                          remove
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {sh.notes && (
                        <div className="mt-2 whitespace-pre-wrap text-xs text-gray-bold">
                          Notes shown to the booker: {sh.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default OfferTimesCard;
